import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/** The API's limit on each note (`Estimate.NoteMaxLength`), measured after trimming. */
export const NOTE_MAX_LENGTH = 2000;

const TEXTAREA_CLASS =
  'w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

/**
 * The estimate's two notes while it is a draft (M2-07): the message the
 * customer reads on their link, and the notes that never leave the office.
 * Saved by the builder's autosave WITH the lines — this component only edits.
 *
 * ⚠️ No `maxLength`: a paste over the limit would be cut silently, losing the
 * end of somebody's message without a word. The count turns red instead, and
 * the API's own sentence arrives with the save's refusal (`errors`).
 */
export function EstimateNotesEditor({
  customerMessage,
  internalNotes,
  onCustomerMessageChange,
  onInternalNotesChange,
  errors,
}: {
  customerMessage: string;
  internalNotes: string;
  onCustomerMessageChange: (value: string) => void;
  onInternalNotesChange: (value: string) => void;
  /** The save's 400, keyed as the API keys it (`CustomerMessage`, `InternalNotes`). */
  errors: Record<string, string[]>;
}) {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="estimate-notes-heading" className="space-y-4">
      <h2 id="estimate-notes-heading" className="text-base font-semibold">
        {t('estimates.notes.heading')}
      </h2>
      <NoteField
        label={t('estimates.notes.customerMessage')}
        hint={t('estimates.notes.customerMessageHint')}
        value={customerMessage}
        onChange={onCustomerMessageChange}
        error={errors.CustomerMessage?.[0]}
      />
      <NoteField
        label={t('estimates.notes.internalNotes')}
        hint={t('estimates.notes.internalNotesHint')}
        value={internalNotes}
        onChange={onInternalNotesChange}
        error={errors.InternalNotes?.[0]}
      />
    </section>
  );
}

function NoteField({
  label,
  hint,
  value,
  onChange,
  error,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  error: string | undefined;
}) {
  const { t } = useTranslation();
  const id = useId();
  const used = value.trim().length;
  const over = used > NOTE_MAX_LENGTH;

  return (
    <div className="space-y-1">
      <label htmlFor={`${id}-field`} className="block text-sm font-medium">
        {label}
      </label>
      <p id={`${id}-hint`} className="text-xs text-[var(--color-muted-foreground)]">
        {hint}
      </p>
      <textarea
        id={`${id}-field`}
        rows={3}
        className={TEXTAREA_CLASS}
        value={value}
        aria-invalid={error || over ? true : undefined}
        aria-describedby={[`${id}-hint`, `${id}-count`, error ? `${id}-error` : null]
          .filter(Boolean)
          .join(' ')}
        onChange={(event) => onChange(event.target.value)}
      />
      <p
        id={`${id}-count`}
        className={cn(
          'text-right text-xs tabular-nums',
          over ? 'text-[var(--color-destructive)]' : 'text-[var(--color-muted-foreground)]',
        )}
      >
        {t('estimates.notes.count', { used, max: NOTE_MAX_LENGTH })}
      </p>
      {error ? (
        <p id={`${id}-error`} className="text-sm text-[var(--color-destructive)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The notes once the estimate is sent: read-only, frozen with the price
 * (M2-07). A revision carries both over for editing (the API copies them).
 */
export function EstimateNotesView({
  customerMessage,
  internalNotes,
}: {
  customerMessage: string | null;
  internalNotes: string | null;
}) {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="estimate-notes-heading" className="space-y-3">
      <h2 id="estimate-notes-heading" className="text-base font-semibold">
        {t('estimates.notes.heading')}
      </h2>
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="font-medium">{t('estimates.notes.customerMessage')}</dt>
          <dd className="whitespace-pre-line" data-testid="customer-message">
            {customerMessage || (
              <span className="text-[var(--color-muted-foreground)]">
                {t('estimates.notes.none')}
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="font-medium">{t('estimates.notes.internalNotes')}</dt>
          <dd className="whitespace-pre-line" data-testid="internal-notes">
            {internalNotes || (
              <span className="text-[var(--color-muted-foreground)]">
                {t('estimates.notes.none')}
              </span>
            )}
          </dd>
        </div>
      </dl>
    </section>
  );
}
